import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button, Alert,
  IconButton, Divider, FormControlLabel, Switch, Grid, Chip, CircularProgress
} from '@mui/material';
import { Home, Email, Send, Save, CheckCircle } from '@mui/icons-material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;

const empty = {
  enabled: false, host: '', port: 587, secure: false, user: '',
  pass: '', fromName: '', fromEmail: '', replyTo: '',
};

const MailSetupPage = () => {
  const navigate = useNavigate();
  const [form,    setForm]    = useState(empty);
  const [passSet, setPassSet] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [testTo,  setTestTo]  = useState('');
  const [testing, setTesting] = useState(false);
  const [msg,     setMsg]     = useState({ type: '', text: '' });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    axios.get(`${API}/api/setup/mail-setup`, { headers })
      .then(({ data }) => {
        setForm({ ...empty, ...data, pass: '' });
        setPassSet(!!data.passSet);
        if (data.fromEmail) setTestTo(data.fromEmail);
      })
      .catch(() => setMsg({ type: 'error', text: 'Failed to load mail setup' }))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    setMsg({ type: '', text: '' });
    try {
      const { data } = await axios.put(`${API}/api/setup/mail-setup`, form, { headers });
      setPassSet(!!data.passSet);
      setForm(f => ({ ...f, pass: '' }));
      setMsg({ type: 'success', text: 'Mail setup saved' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testTo.trim()) { setMsg({ type: 'error', text: 'Enter a recipient for the test email' }); return; }
    setTesting(true);
    setMsg({ type: '', text: '' });
    try {
      const { data } = await axios.post(`${API}/api/setup/mail-setup/test`, { to: testTo.trim() }, { headers });
      setMsg({ type: 'success', text: data.message || 'Test email sent' });
    } catch (err) {
      setMsg({ type: 'error', text: err.response?.data?.error || 'Failed to send test email' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, textAlign: 'center' }}><CircularProgress /></Box>
  );

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Email color="primary" />
              <Typography variant="h5" color="primary">Email / SMTP Setup</Typography>
            </Box>
            <IconButton onClick={() => navigate('/setups')} title="Setups Home"
              sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
              <Home />
            </IconButton>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            Configure your school's own mailbox so purchase orders, receipts and notifications are sent
            <strong> from your address</strong>, with replies coming back to you. Leave this <em>off</em> and mail
            still goes out through the shared EduMIS mailbox. Use an <strong>app password</strong> (e.g. a Google
            Workspace / Microsoft 365 app password), never your main account password.
          </Alert>

          {msg.text && <Alert severity={msg.type || 'info'} sx={{ mb: 2 }}>{msg.text}</Alert>}

          <FormControlLabel
            control={<Switch checked={form.enabled} onChange={e => set('enabled', e.target.checked)} />}
            label={<Typography fontWeight={600}>Send from my school's own email account</Typography>}
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="primary" gutterBottom>SMTP Server</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField label="SMTP Host" value={form.host} onChange={e => set('host', e.target.value)}
                fullWidth size="small" placeholder="smtp.gmail.com" />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField label="Port" type="number" value={form.port} onChange={e => set('port', e.target.value)}
                fullWidth size="small" placeholder="587" />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField label="Username" value={form.user} onChange={e => set('user', e.target.value)}
                fullWidth size="small" placeholder="mail@yourschool.ac.ke" autoComplete="off" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Password / App Password" type="password" value={form.pass}
                onChange={e => set('pass', e.target.value)} fullWidth size="small" autoComplete="new-password"
                placeholder={passSet ? '•••••• (leave blank to keep)' : 'app password'}
                helperText={passSet
                  ? <Box component="span" display="inline-flex" alignItems="center" gap={0.5}>
                      <CheckCircle sx={{ fontSize: 14 }} color="success" /> Password stored
                    </Box>
                  : 'Stored encrypted'} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={form.secure} onChange={e => set('secure', e.target.checked)} />}
                label="Use implicit TLS (port 465). Off = STARTTLS (port 587)." />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" color="primary" gutterBottom>Sender Identity</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="From Name" value={form.fromName} onChange={e => set('fromName', e.target.value)}
                fullWidth size="small" placeholder="Dataport Academy" helperText="Shown as the sender name" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="From Email" value={form.fromEmail} onChange={e => set('fromEmail', e.target.value)}
                fullWidth size="small" placeholder="info@yourschool.ac.ke"
                helperText="Defaults to the username" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Reply-To" value={form.replyTo} onChange={e => set('replyTo', e.target.value)}
                fullWidth size="small" placeholder="admin@yourschool.ac.ke"
                helperText="Where replies should land" />
            </Grid>
          </Grid>

          <Box display="flex" gap={2} mt={3}>
            <Button variant="contained" startIcon={<Save />} onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save Setup'}
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" color="primary" gutterBottom>Send a Test Email</Typography>
          <Typography variant="body2" color="text.secondary" mb={1}>
            Sends a test message using the saved settings ({passSet || form.enabled ? 'your account' : 'the shared mailbox'}).
            Save first if you've just changed anything.
          </Typography>
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <TextField label="Send test to" value={testTo} onChange={e => setTestTo(e.target.value)}
              size="small" sx={{ minWidth: 280 }} placeholder="you@example.com" />
            <Button variant="outlined" startIcon={<Send />} onClick={sendTest} disabled={testing}>
              {testing ? 'Sending…' : 'Send Test'}
            </Button>
            {form.enabled
              ? <Chip size="small" color="success" label="Using your account" />
              : <Chip size="small" label="Using shared mailbox" variant="outlined" />}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default MailSetupPage;
